/**
 * Client-side .ics generation + Google Calendar URL builder for
 * Carnival events. Kept on the frontend because carnivalMeta.js is a
 * static module — a backend round-trip would just be a passthrough.
 *
 * Honesty rules (mandatory, matches the JSON-LD honesty gate):
 *   - Estimated events get `STATUS:TENTATIVE` in the .ics + an
 *     explicit "(estimated date, subject to change)" tag appended to
 *     both SUMMARY and DESCRIPTION.
 *   - Confirmed events get `STATUS:CONFIRMED` and no tentative tag.
 *
 * The event's `time_local` is treated as local time in the island's
 * AST timezone (UTC-04:00, no DST in the Dutch Caribbean). Duration
 * defaults to 3 hours — enough for parade-length events, and calendar
 * clients let users adjust after import.
 */

const AST_OFFSET_MIN = -4 * 60; // Atlantic Standard Time, no DST

const pad2 = (n) => String(n).padStart(2, "0");

// "2027-04-24" + "20:00" → "20270424T200000" (local naive form used
// by .ics with a TZID). We also emit UTC-anchored DTSTART/DTEND so
// clients that don't respect our VTIMEZONE block still land within
// the right hour.
const toIcsLocal = (isoDate, timeLocal) => {
  const [y, m, d] = isoDate.split("-").map(Number);
  const [hh, mm] = (timeLocal || "12:00").split(":").map(Number);
  return `${y}${pad2(m)}${pad2(d)}T${pad2(hh)}${pad2(mm)}00`;
};

const addHours = (isoDate, timeLocal, hours) => {
  const [y, m, d] = isoDate.split("-").map(Number);
  const [hh, mm] = (timeLocal || "12:00").split(":").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d, hh + hours, mm) - AST_OFFSET_MIN * 60 * 1000);
  // Return as YYYYMMDDTHHMM00 UTC form for the Z-suffixed variant.
  return (
    `${dt.getUTCFullYear()}${pad2(dt.getUTCMonth() + 1)}${pad2(dt.getUTCDate())}` +
    `T${pad2(dt.getUTCHours())}${pad2(dt.getUTCMinutes())}00Z`
  );
};

const toUtcStamp = (isoDate, timeLocal) => addHours(isoDate, timeLocal, 0);

const escapeIcsText = (s) =>
  String(s || "")
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\n/g, "\\n")
    .replace(/\r/g, "");

const foldLine = (line) => {
  // RFC 5545: fold at 75 chars, continuation lines start with a space.
  if (line.length <= 75) return line;
  const out = [];
  let i = 0;
  while (i < line.length) {
    out.push((i === 0 ? "" : " ") + line.slice(i, i + 75));
    i += 75;
  }
  return out.join("\r\n");
};

/**
 * Build a full .ics VCALENDAR string for a single event.
 * Args:
 *   { islandKey, event, label, description, siteOrigin, lang }
 * Returns a UTF-8 string; caller wraps into a Blob and triggers download.
 */
export const buildEventIcs = ({
  islandKey,
  event,
  label,
  description,
  siteOrigin = "https://islandsdaily.com",
  estimatedNote = "estimated date, subject to change",
}) => {
  if (!event?.date) return null;
  const isEstimated = event.status !== "confirmed";
  const summary = isEstimated ? `${label} (${estimatedNote})` : label;
  const descBase = description || label;
  const fullDesc = isEstimated
    ? `${descBase}\n\n(${estimatedNote}. Confirm with the organizer before travelling.)`
    : descBase;

  const dtStartUtc = toUtcStamp(event.date, event.time_local);
  const dtEndUtc = addHours(event.date, event.time_local, 3);
  const now = new Date();
  const stamp =
    `${now.getUTCFullYear()}${pad2(now.getUTCMonth() + 1)}${pad2(now.getUTCDate())}` +
    `T${pad2(now.getUTCHours())}${pad2(now.getUTCMinutes())}${pad2(now.getUTCSeconds())}Z`;
  const uid = `${islandKey}-${event.key}-${event.date}@islandsdaily.com`;
  const url = `${siteOrigin}/${islandKey}/carnival`;
  const location = event.location?.name
    ? `${event.location.name}${event.location.city ? ", " + event.location.city : ""}`
    : (event.location?.city || "");

  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//IslandsDaily//Carnival//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${stamp}`,
    `DTSTART:${dtStartUtc}`,
    `DTEND:${dtEndUtc}`,
    `SUMMARY:${escapeIcsText(summary)}`,
    `DESCRIPTION:${escapeIcsText(fullDesc)}`,
    location ? `LOCATION:${escapeIcsText(location)}` : null,
    `URL:${url}`,
    `STATUS:${isEstimated ? "TENTATIVE" : "CONFIRMED"}`,
    `TRANSP:OPAQUE`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].filter(Boolean).map(foldLine);
  return lines.join("\r\n") + "\r\n";
};

/**
 * Trigger a browser download of the .ics blob. Called after the user
 * clicks "Add to Calendar → Apple / Outlook".
 */
export const downloadIcs = (icsString, filename) => {
  if (!icsString || typeof window === "undefined") return;
  const blob = new Blob([icsString], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename.endsWith(".ics") ? filename : `${filename}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 4000);
};

/**
 * Build a Google Calendar prefill URL. Users tapping this get sent
 * to calendar.google.com with a "Save event" form already populated.
 * Format ref: https://calendar.google.com/calendar/render?action=TEMPLATE&...
 */
export const buildGoogleCalendarUrl = ({
  event,
  label,
  description,
  islandKey,
  siteOrigin = "https://islandsdaily.com",
  estimatedNote = "estimated date, subject to change",
}) => {
  if (!event?.date) return null;
  const isEstimated = event.status !== "confirmed";
  const title = isEstimated ? `${label} (${estimatedNote})` : label;
  const descBase = description || label;
  const details = isEstimated
    ? `${descBase}\n\n(${estimatedNote}. Confirm with the organizer before travelling.)\n\n${siteOrigin}/${islandKey}/carnival`
    : `${descBase}\n\n${siteOrigin}/${islandKey}/carnival`;
  const dtStart = toIcsLocal(event.date, event.time_local);
  const [, , dEnd, tEnd] = toUtcStamp(event.date, event.time_local).match(
    /^(\d{8})T(\d{6})Z$/
  ) || [];
  // Google Calendar accepts UTC form with Z suffix: YYYYMMDDTHHMMSSZ.
  const startUtc = toUtcStamp(event.date, event.time_local);
  const endUtc = addHours(event.date, event.time_local, 3);
  const dates = `${startUtc}/${endUtc}`;
  const location = event.location?.name
    ? `${event.location.name}${event.location.city ? ", " + event.location.city : ""}`
    : (event.location?.city || "");
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: title,
    dates,
    details,
    location,
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};
